import { ReactSVG } from "react-svg"
import dynamic from "next/dynamic"
import classNames from "classnames"

// ---------------------------------------------------------

import RenderBlocks from "@lib/renderBlocks"
import { getDate, parameterize } from "@lib/utils"
import Layout from "../../layout"

// ---------------------------------------------------------

import Button from "@components/button"
import Content from "@components/content"
import Image from "@components/image"

const MarketoForm = dynamic(() => import("@components/marketoForm"), { ssr: false })
const Speakers = dynamic(() => import("@components/speakers"), { ssr: false })
const Video = dynamic(() => import("@components/video"), { ssr: false })
const RecommendedReading = dynamic(() => import("@components/recommended-reading"), { ssr: false })

// ---------------------------------------------------------

import {
  default_event_template,
  column_left,
  column_right,
  column_right_content,
  featured_image,
  post_header,
  post_header_details,
  post_header_details_icon,
  post_header_label
} from "./styles.module.scss"

// ---------------------------------------------------------

const DefaultEventTemplate = ({ data, recommended, preview }) => {
  const {
    title,
    excerpt,
    resourceType,
    featuredImage,
    featuredVideo,
    startDate,
    endDate,
    location,
    pageContent,
    form,
    button,
    modulesCollection,
    speakersCollection,
    timeZoneOverride,
    isPrivate,
    demoLabel,
    demoUrl,
    hideTemplate,
    navSettings
  } = data

  const meta = {
    title: title,
    description: excerpt,
    robots: isPrivate ? "noindex" : "index"
  }

  const isGated = data?.form

  // ---------------------------------------------------------

  /**
   * @returns Copy, icons, and date/time details in the hero of the ping pong.
   */
  const getHeader = () => {
    return (
      <header className={post_header}>
        <span className={post_header_label}>
          <p>{resourceType}</p>
        </span>
        <h1>{title}</h1>
        <div>
          {location ? (
            <div className={post_header_details}>
              <ReactSVG
                className={classNames(post_header_details_icon)}
                src={`/icons/location.svg`}
              />
              {location}
            </div>
          ) : null}
          {startDate && (
            <div className={post_header_details}>
              <ReactSVG
                className={classNames(post_header_details_icon)}
                src={`/icons/calendar.svg`}
              />
              {timeZoneOverride ? timeZoneOverride : getDate(startDate, endDate)}
            </div>
          )}
        </div>
        {button && (
          <div>
            <Button
              theme={parameterize(button.theme)}
              buttonLabel={button.labelAttr}
              url={button.url}
            >
              {button.labelAttr}
            </Button>
          </div>
        )}
      </header>
    )
  }

  /**
   * @returns Featured video or video for the resource, if it exists.
   */
  const getMedia = () => {
    if (featuredVideo?.videoUrl) return <Video {...featuredVideo} />
    return (
      <Image
        className={featured_image}
        height={featuredImage.height}
        width={featuredImage.width}
        src={featuredImage.url}
        alt={featuredImage.description}
        sizes="100vw"
      />
    )
  }

  // ---------------------------------------------------------

  return (
    <Layout
      preview={preview}
      pageTitle={title}
      featuredImage={featuredImage}
      customMeta={meta}
      navSettings={{
        hideNavLeft: navSettings?.includes("Hide left nav"),
        hideNavRight: navSettings?.includes("Hide right nav"),
        customCta: { label: demoLabel, url: demoUrl }
      }}
    >
      {!hideTemplate && (
        <div className={default_event_template} id="hero">
          <div className={column_left}>
            {getHeader()}
            {isGated && getMedia()}
            {pageContent && <Content content={pageContent} data-cy="post-content-container" />}
            {speakersCollection?.items?.length > 0 && (
              <Speakers speakersCollection={speakersCollection} />
            )}
          </div>

          <div className={column_right}>
            {!isGated && getMedia()}
            {form && <MarketoForm className={column_right_content} {...form} />}
          </div>
        </div>
      )}

      {modulesCollection?.items && RenderBlocks(modulesCollection.items)}
      <RecommendedReading recommended={recommended} />
    </Layout>
  )
}

export default DefaultEventTemplate
